import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { useState } from "react";
import { subscribe } from "../lib/api";
import { submitToNetlify } from "../lib/netlify";
import { subscribeToMailchimp } from "../lib/mailchimp";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);

    // Primary destination is Mailchimp (list-manage). We also submit to
    // Netlify and the backend as internal records / dev backups.
    const [mailchimpResult, netlifyResult, backendResult] = await Promise.allSettled([
      subscribeToMailchimp(email),
      submitToNetlify("newsletter", { email, source: "footer" }),
      subscribe(email, "footer"),
    ]);
    const mailchimpOk = mailchimpResult.status === "fulfilled" && mailchimpResult.value === true;
    const netlifyOk = netlifyResult.status === "fulfilled" && netlifyResult.value === true;
    const backendOk = backendResult.status === "fulfilled";

    if (mailchimpOk || netlifyOk || backendOk) {
      toast.success("Welcome to the table.");
      setEmail("");
    } else {
      toast.error("Couldn't reach us. Email salumitucson@gmail.com and we'll add you.");
    }
    setLoading(false);
  };

  return (
    <footer data-testid="site-footer" className="relative bg-espresso text-ivory overflow-hidden">
      <div className="absolute inset-0 light-leak opacity-50" aria-hidden="true" />
      <div className="relative max-w-[1480px] mx-auto px-6 md:px-10 pt-24 pb-12">
        {/* Big footer logo */}
        <motion.div
          initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center text-center mb-20 md:mb-24"
        >
          <img
            src="/media/salumi-logo-trans.png"
            alt="Salumi Tucson"
            className="h-40 md:h-56 w-auto drop-shadow-[0_8px_28px_rgba(0,0,0,0.45)]"
            loading="lazy"
          />
          <p className="mt-6 text-[11px] uppercase tracking-[0.4em] text-ivory/60">
            Tucson · Arizona · Sonoran Desert
          </p>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-12 md:gap-16 mb-20">
          <div className="md:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-6">Stay close to the table</p>
            <h3 className="font-serif text-4xl md:text-5xl tracking-editorial mb-8 leading-[0.95]">
              Join the Salumi <em className="not-italic text-terracotta">guest list</em>.
            </h3>
            <form
              onSubmit={handleSubscribe}
              data-testid="footer-newsletter-form"
              name="newsletter"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="flex items-end gap-4 border-b border-ivory/30 pb-3"
            >
              <input type="hidden" name="form-name" value="newsletter" />
              <input type="hidden" name="bot-field" />
              <input
                type="email"
                name="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent border-0 outline-none text-lg placeholder-ivory/40 text-ivory py-2"
                data-testid="footer-email-input"
                required
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-subscribe-button"
                className="text-[11px] uppercase tracking-[0.22em] text-ivory/80 hover:text-terracotta transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Subscribe →"}
              </button>
            </form>
            <p className="mt-4 text-xs text-ivory/40">
              Seasonal announcements. Early booking access. Never spam.
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-6">Explore</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="editorial-underline hover:text-terracotta">Home</Link></li>
              <li><Link to="/classes" className="editorial-underline hover:text-terracotta">Open Classes</Link></li>
              <li><Link to="/private-bookings" className="editorial-underline hover:text-terracotta">Private Bookings</Link></li>
              <li><Link to="/gallery" className="editorial-underline hover:text-terracotta">Gallery</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.3em] text-olive mb-6">Find us</p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin strokeWidth={1.25} className="size-4 mt-1 text-terracotta" />
                <span>Tucson, Arizona<br /><span className="text-ivory/60">Sonoran Desert</span></span>
              </li>
              <li className="flex items-center gap-3">
                <Mail strokeWidth={1.25} className="size-4 text-terracotta" />
                <a href="mailto:salumitucson@gmail.com" className="editorial-underline">salumitucson@gmail.com</a>
              </li>
              <li className="flex items-center gap-3">
                <Instagram strokeWidth={1.25} className="size-4 text-terracotta" />
                <a
                  href="https://www.instagram.com/salumitucson/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="editorial-underline"
                  data-testid="footer-instagram-link"
                >
                  @SalumiTucson
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-ivory/15 pt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-ivory/40">© {new Date().getFullYear()} Salumi Tucson</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-ivory/40 mt-1">Crafted in the Sonoran Desert</p>
          </div>
          <div className="md:text-right">
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-ivory/40"
              data-testid="footer-photo-credit"
            >
              All photos by Salumi Tucson or{" "}
              <a
                href="https://www.JMartinHarris.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ivory/60 hover:text-terracotta editorial-underline"
              >
                J. Martin Harris Photography
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Massive wordmark */}
      <div className="relative overflow-hidden">
        <h2
          aria-hidden="true"
          className="font-serif text-ivory/[0.06] leading-none whitespace-nowrap select-none px-2"
          style={{ fontSize: "clamp(7rem, 28vw, 24rem)", letterSpacing: "-0.04em" }}
        >
          SALUMI
        </h2>
      </div>
    </footer>
  );
}
